/* eslint-disable camelcase */
import _ from 'underscore';
import config from '@bingads-webui-component-legacy/config/index';

import {
  Shared,
  Columns,
  AccountPerformanceReportUIPreviewV1,
  CampaignPerformanceReportUIPreviewV1,
  AdGroupPerformanceReportUIPreviewV1,
  SearchQueryPerformanceReportUIPreviewV1,
  KeywordPerformanceReportUIPreviewV1,
  AccountPerformanceReport_ShareOfVoiceUIPreviewV1,
  AccountPerformanceReport_SixWeeksUIPreviewV1,
  AccountPerformanceReport_TwoMonthsUIPreviewV1,
  AccountPerformanceReport_TwoYearsUIPreviewV1,
  AdDynamicTextPerformanceReportUIPreviewV1,
  AdExtensionByAdReportUIPreviewV1,
  AdExtensionByKeywordReportUIPreviewV1,
  AdExtensionDetailReportUIPreviewV1,
  AdPerformanceReportUIPreviewV1,
  AgeGenderAudienceReportUIPreviewV1,
  AudiencePerformanceReportUIPreviewV1,
  CallDetailReportUIPreviewV1,
  ConversionPerformanceReportUIPreviewV1,
  DestinationURLPerformanceReportUIPreviewV1,
  DSAAutoTargetPerformanceReportUIPreviewV1,
  DSACategoryPerformanceReportUIPreviewV1,
  DSASearchQueryPerformanceReportUIPreviewV1,
  FraudChangeHistoryReportUIPreviewV1,
  GeographicPerformanceReportUIPreviewV1,
  GoalsAndFunnelsReportUIPreviewV1,
  NegativeKeywordConflictReportUIPreviewV1,
  ProductDimensionPerformanceReportUIPreviewV1,
  ProductMatchCountReportUIPreviewV1,
  ProductNegativeKeywordConflictReportUIPreviewV1,
  ProductPartitionPerformanceReportUIPreviewV1,
  ProductPartitionUnitPerformanceReportUIPreviewV1,
  ProductSearchQueryPerformanceReportUIPreviewV1,
  ProfessionalDemographicsAudienceReportUIPreviewV1,
  PublisherUsagePerformanceReportUIPreviewV1,
  SearchCampaignChangeHistoryReportUIPreviewV1,
  ShareOfVoiceReportUIPreviewV1,
  UserLocationPerformanceReportUIPreviewV1,
  UserRoleChangeHistoryReportUIPreviewV1,
  LabelCampaignReportUIPreviewV1,
  LabelAdGroupReportUIPreviewV1,
  LabelAccountReportUIPreviewV1,
  LabelKeywordReportUIPreviewV1,
  LabelAdReportUIPreviewV1,
  BudgetSummaryReportUIPreviewV1,
  BillingStatementReportUIPreviewV1,
  InsertionOrderReportUIPreviewV1,
  CustomerInvoiceReportUIPreviewV1,
  HotelDimensionPerformanceReportUIPreviewV1,
  HotelGroupPerformanceReportUIPreviewV1,
  FeedItemPerformanceReportUIPreviewV1,
  ConversionModelCompareReportUIPreviewV1,
  MSXAccountPerformanceReportUIPreviewV1,
  GenrePerformanceReportUIPreviewV1,
  AppsPerformanceReportUIPreviewV1,
  AssetPerformanceReportUIPreviewV1,
  SearchInsightPerformanceReportUIPreviewV1,
  SearchVerticalCategoryClickShareReportUIPreviewV1,
  SearchVerticalCategoryInsightsReportUIPreviewV1,
  CombinationPerformanceReportUIPreviewV1,
} from '@bingads-webui/report-ui-interface-preview';
import { i18nMap } from '@bingads-webui-campaign/report-shared';
import { elevatedColumnsMap } from './columns/elevated-columns';
import { restrictedColumnsByReport, defaultColumnsByReport, restrictedFilterColumnsByReport, restrictedDisplayColumnsByReport } from './columns/piloted-columns';
import { helpLinkIds, helpLinkIdsByReportType } from './columns/help-link-ids';
import { missingMandatoryColumnErrors } from './columns/missing-mandatory-column-error';
import { processSchema } from './schema-processor';
import { timeRangeOverride } from './overrides/time-range-override';

const dynamicPermissions = _.result(config.get('permissions'), 'dynamic', {});
const { IsRenameHotelToLodgingEnabled, CPSReporting } = dynamicPermissions;

if (IsRenameHotelToLodgingEnabled) {
  i18nMap.ColumnName.AdvertiserHotelId = _TL_('Advertiser property ID');
  i18nMap.ColumnName.CampaignHotelId = _TL_('Campaign property ID');
  i18nMap.ColumnName.HotelGroup = _TL_('Property group');
  i18nMap.ColumnName.HotelGroupNodeId = _TL_('Property group node id');
  i18nMap.ColumnName.SourceHotelId = _TL_('Source property ID');
  i18nMap.ColumnName.HotelName = _TL_('Property name');
  helpLinkIds.CampaignHotelId = 'pop_BA_Reporting_Clarity_CampaignPropertyId';
  helpLinkIds.AdvertiserHotelId = 'pop_BA_Reporting_Clarity_AdvertiserPropertyId';
  helpLinkIds.HotelGroup = 'pop_BA_Reporting_Clarity_PropertyGroup';
  helpLinkIds.HotelGroupNodeId = 'pop_BA_Reporting_Clarity_PropertyGroupNodeId';
}

/* istanbul ignore if  */
if (CPSReporting) {
  i18nMap.ColumnName.CostPerSale = _TL_('Avg. CPS');
  i18nMap.ColumnName.Sales = _TL_('Sales Count');
}

const process = _.memoize(
  rawSchema =>
    processSchema(
      rawSchema,
      Shared,
      elevatedColumnsMap[rawSchema.name],
      restrictedColumnsByReport[rawSchema.name] || [],
      defaultColumnsByReport[rawSchema.name] || [],
      i18nMap.ColumnName,
      { ...helpLinkIds, ...helpLinkIdsByReportType[rawSchema.name] },
      timeRangeOverride[rawSchema.name],
      missingMandatoryColumnErrors[rawSchema.name],
      restrictedFilterColumnsByReport[rawSchema.name] || [],
      restrictedDisplayColumnsByReport[rawSchema.name] || []
    ),
  reportSchema => reportSchema.name
);

/* eslint-disable max-len */
export const AccountPerformanceReport = () => process(AccountPerformanceReportUIPreviewV1);
export const AccountPerformanceReport_SixWeeks = () => process(AccountPerformanceReport_SixWeeksUIPreviewV1);
export const AccountPerformanceReport_TwoMonths = () => process(AccountPerformanceReport_TwoMonthsUIPreviewV1);
export const AccountPerformanceReport_TwoYears = () => process(AccountPerformanceReport_TwoYearsUIPreviewV1);
export const AccountPerformanceReport_ShareOfVoice = () => process(AccountPerformanceReport_ShareOfVoiceUIPreviewV1);
export const AdDynamicTextPerformanceReport = () => process(AdDynamicTextPerformanceReportUIPreviewV1);
export const AdExtensionByAdReport = () => process(AdExtensionByAdReportUIPreviewV1);
export const AdExtensionByKeywordReport = () => process(AdExtensionByKeywordReportUIPreviewV1);
export const AdExtensionDetailReport = () => process(AdExtensionDetailReportUIPreviewV1);
export const AdGroupPerformanceReport = () => process(AdGroupPerformanceReportUIPreviewV1);
export const AdPerformanceReport = () => process(AdPerformanceReportUIPreviewV1);
export const AgeGenderAudienceReport = () => process(AgeGenderAudienceReportUIPreviewV1);
export const AppsPerformanceReport = () => process(AppsPerformanceReportUIPreviewV1);
export const AssetPerformanceReport = () => process(AssetPerformanceReportUIPreviewV1);
export const AudiencePerformanceReport = () => process(AudiencePerformanceReportUIPreviewV1);
export const CallDetailReport = () => process(CallDetailReportUIPreviewV1);
export const CampaignPerformanceReport = () => process(CampaignPerformanceReportUIPreviewV1);
export const CombinationPerformanceReport = () => process(CombinationPerformanceReportUIPreviewV1);
export const ConversionPerformanceReport = () => process(ConversionPerformanceReportUIPreviewV1);
export const ConversionModelCompareReport = () => process(ConversionModelCompareReportUIPreviewV1);
export const MSXAccountPerformanceReport = () => process(MSXAccountPerformanceReportUIPreviewV1);
export const DSAAutoTargetPerformanceReport = () => process(DSAAutoTargetPerformanceReportUIPreviewV1);
export const DSACategoryPerformanceReport = () => process(DSACategoryPerformanceReportUIPreviewV1);
export const DSASearchQueryPerformanceReport = () => process(DSASearchQueryPerformanceReportUIPreviewV1);
export const DestinationURLPerformanceReport = () => process(DestinationURLPerformanceReportUIPreviewV1);
export const FraudChangeHistoryReport = () => process(FraudChangeHistoryReportUIPreviewV1);
export const GeographicPerformanceReport = () => process(GeographicPerformanceReportUIPreviewV1);
export const GoalsAndFunnelsReport = () => process(GoalsAndFunnelsReportUIPreviewV1);
export const KeywordPerformanceReport = () => process(KeywordPerformanceReportUIPreviewV1);
export const NegativeKeywordConflictReport = () => process(NegativeKeywordConflictReportUIPreviewV1);
export const ProductDimensionPerformanceReport = () => process(ProductDimensionPerformanceReportUIPreviewV1);
export const ProductMatchCountReport = () => process(ProductMatchCountReportUIPreviewV1);
export const ProductNegativeKeywordConflictReport = () => process(ProductNegativeKeywordConflictReportUIPreviewV1);
export const ProductPartitionPerformanceReport = () => process(ProductPartitionPerformanceReportUIPreviewV1);
export const ProductPartitionUnitPerformanceReport = () => process(ProductPartitionUnitPerformanceReportUIPreviewV1);
export const ProductSearchQueryPerformanceReport = () => process(ProductSearchQueryPerformanceReportUIPreviewV1);
export const ProfessionalDemographicsAudienceReport = () => process(ProfessionalDemographicsAudienceReportUIPreviewV1);
export const PublisherUsagePerformanceReport = () => process(PublisherUsagePerformanceReportUIPreviewV1);
export const SearchCampaignChangeHistoryReport = () => process(SearchCampaignChangeHistoryReportUIPreviewV1);
export const SearchQueryPerformanceReport = () => process(SearchQueryPerformanceReportUIPreviewV1);
export const ShareOfVoiceReport = () => process(ShareOfVoiceReportUIPreviewV1);
export const UserLocationPerformanceReport = () => process(UserLocationPerformanceReportUIPreviewV1);
export const UserRoleChangeHistoryReport = () => process(UserRoleChangeHistoryReportUIPreviewV1);
export const LabelCampaignReport = () => process(LabelCampaignReportUIPreviewV1);
export const LabelAdGroupReport = () => process(LabelAdGroupReportUIPreviewV1);
export const LabelAccountReport = () => process(LabelAccountReportUIPreviewV1);
export const LabelKeywordReport = () => process(LabelKeywordReportUIPreviewV1);
export const LabelAdReport = () => process(LabelAdReportUIPreviewV1);
export const BudgetSummaryReport = () => process(BudgetSummaryReportUIPreviewV1);
export const BillingStatementReport = () => process(BillingStatementReportUIPreviewV1);
export const InsertionOrderReport = () => process(InsertionOrderReportUIPreviewV1);
export const CustomerInvoiceReport = () => process(CustomerInvoiceReportUIPreviewV1);
export const HotelDimensionPerformanceReport = () => process(HotelDimensionPerformanceReportUIPreviewV1);
export const HotelGroupPerformanceReport = () => process(HotelGroupPerformanceReportUIPreviewV1);
export const FeedItemPerformanceReport = () => process(FeedItemPerformanceReportUIPreviewV1);
export const GenrePerformanceReport = () => process(GenrePerformanceReportUIPreviewV1);
export const SearchInsightPerformanceReport = () => process(SearchInsightPerformanceReportUIPreviewV1);
export const SearchVerticalCategoryInsightsReport = () => process(SearchVerticalCategoryInsightsReportUIPreviewV1);
export const SearchVerticalCategoryClickShareReport = () => process(SearchVerticalCategoryClickShareReportUIPreviewV1);

export const schema = {};
Object.defineProperties(schema, {
  AccountPerformanceReport: { get: AccountPerformanceReport, enumerable: true },
  'AccountPerformanceReport-SixWeeks': { get: AccountPerformanceReport_SixWeeks, enumerable: true },
  'AccountPerformanceReport-TwoMonths': { get: AccountPerformanceReport_TwoMonths, enumerable: true },
  'AccountPerformanceReport-TwoYears': { get: AccountPerformanceReport_TwoYears, enumerable: true },
  'AccountPerformanceReport-ShareOfVoice': { get: AccountPerformanceReport_ShareOfVoice, enumerable: true },
  AdDynamicTextPerformanceReport: { get: AdDynamicTextPerformanceReport, enumerable: true },
  AdExtensionByAdReport: { get: AdExtensionByAdReport, enumerable: true },
  AdExtensionByKeywordReport: { get: AdExtensionByKeywordReport, enumerable: true },
  AdExtensionDetailReport: { get: AdExtensionDetailReport, enumerable: true },
  AdGroupPerformanceReport: { get: AdGroupPerformanceReport, enumerable: true },
  AdPerformanceReport: { get: AdPerformanceReport, enumerable: true },
  AgeGenderAudienceReport: { get: AgeGenderAudienceReport, enumerable: true },
  AppsPerformanceReport: { get: AppsPerformanceReport, enumerable: true },
  AssetPerformanceReport: { get: AssetPerformanceReport, enumerable: true },
  AudiencePerformanceReport: { get: AudiencePerformanceReport, enumerable: true },
  CallDetailReport: { get: CallDetailReport, enumerable: true },
  CampaignPerformanceReport: { get: CampaignPerformanceReport, enumerable: true },
  CombinationPerformanceReport: { get: CombinationPerformanceReport, enumerable: true },
  ConversionPerformanceReport: { get: ConversionPerformanceReport, enumerable: true },
  ConversionModelCompareReport: { get: ConversionModelCompareReport, enumerable: true },
  MSXAccountPerformanceReport: { get: MSXAccountPerformanceReport, enumerable: true },
  DSAAutoTargetPerformanceReport: { get: DSAAutoTargetPerformanceReport, enumerable: true },
  DSACategoryPerformanceReport: { get: DSACategoryPerformanceReport, enumerable: true },
  DSASearchQueryPerformanceReport: { get: DSASearchQueryPerformanceReport, enumerable: true },
  DestinationURLPerformanceReport: { get: DestinationURLPerformanceReport, enumerable: true },
  FraudChangeHistoryReport: { get: FraudChangeHistoryReport, enumerable: true },
  GeographicPerformanceReport: { get: GeographicPerformanceReport, enumerable: true },
  GoalsAndFunnelsReport: { get: GoalsAndFunnelsReport, enumerable: true },
  KeywordPerformanceReport: { get: KeywordPerformanceReport, enumerable: true },
  NegativeKeywordConflictReport: { get: NegativeKeywordConflictReport, enumerable: true },
  ProductDimensionPerformanceReport: { get: ProductDimensionPerformanceReport, enumerable: true },
  ProductMatchCountReport: { get: ProductMatchCountReport, enumerable: true },
  ProductNegativeKeywordConflictReport: { get: ProductNegativeKeywordConflictReport, enumerable: true },
  ProductPartitionPerformanceReport: { get: ProductPartitionPerformanceReport, enumerable: true },
  ProductPartitionUnitPerformanceReport: { get: ProductPartitionUnitPerformanceReport, enumerable: true },
  ProductSearchQueryPerformanceReport: { get: ProductSearchQueryPerformanceReport, enumerable: true },
  ProfessionalDemographicsAudienceReport: { get: ProfessionalDemographicsAudienceReport, enumerable: true },
  PublisherUsagePerformanceReport: { get: PublisherUsagePerformanceReport, enumerable: true },
  SearchCampaignChangeHistoryReport: { get: SearchCampaignChangeHistoryReport, enumerable: true },
  SearchQueryPerformanceReport: { get: SearchQueryPerformanceReport, enumerable: true },
  ShareOfVoiceReport: { get: ShareOfVoiceReport, enumerable: true },
  UserLocationPerformanceReport: { get: UserLocationPerformanceReport, enumerable: true },
  UserRoleChangeHistoryReport: { get: UserRoleChangeHistoryReport, enumerable: true },
  LabelCampaignReport: { get: LabelCampaignReport, enumerable: true },
  LabelAdGroupReport: { get: LabelAdGroupReport, enumerable: true },
  LabelAccountReport: { get: LabelAccountReport, enumerable: true },
  LabelKeywordReport: { get: LabelKeywordReport, enumerable: true },
  LabelAdReport: { get: LabelAdReport, enumerable: true },
  BudgetSummaryReport: { get: BudgetSummaryReport, enumerable: true },
  BillingStatementReport: { get: BillingStatementReport, enumerable: true },
  InsertionOrderReport: { get: InsertionOrderReport, enumerable: true },
  CustomerInvoiceReport: { get: CustomerInvoiceReport, enumerable: true },
  HotelDimensionPerformanceReport: { get: HotelDimensionPerformanceReport, enumerable: true },
  HotelGroupPerformanceReport: { get: HotelGroupPerformanceReport, enumerable: true },
  FeedItemPerformanceReport: { get: FeedItemPerformanceReport, enumerable: true },
  GenrePerformanceReport: { get: GenrePerformanceReport, enumerable: true },
  SearchInsightPerformanceReport: { get: SearchInsightPerformanceReport, enumerable: true },
  SearchVerticalCategoryInsightsReport: { get: SearchVerticalCategoryInsightsReport, enumerable: true },
  SearchVerticalCategoryClickShareReport: { get: SearchVerticalCategoryClickShareReport, enumerable: true },
  ColumnConfig: { value: Columns.columns },
});
